import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  CircleAlert,
  CreditCard,
  FlaskConical,
  KeyRound,
  Server,
  TriangleAlert,
  Trash2,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Modal, Campo, claseEntrada } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import type { CodigoDePasarela, Pasarela } from './contrato';

const PROVEEDORES: Record<
  CodigoDePasarela,
  { usuario: string; secreto: string; ayuda: string; pruebas: string; produccion: string } | null
> = {
  NUVEI: {
    usuario: 'Código de aplicación (servidor)',
    secreto: 'Clave de aplicación (servidor)',
    ayuda: 'Las credenciales EC-SERVER que entrega Nuvei. Las de cliente no hacen falta.',
    pruebas: 'https://ccapi-stg.paymentez.com/v2',
    produccion: 'https://ccapi.paymentez.com/v2',
  },
  PLACETOPAY: {
    usuario: 'Login del sitio',
    secreto: 'SecretKey del sitio',
    ayuda: 'El login y la secretKey que entrega Evertec para el sitio de Placetopay.',
    pruebas: 'https://checkout-test.placetopay.ec',
    produccion: 'https://checkout.placetopay.ec',
  },
  SIMULADA: null,
};

function fechaYHora(instante: string): string {
  return new Date(instante).toLocaleString('es-EC', {
    timeZone: 'America/Guayaquil',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PaginaPasarelasDePago() {
  const { puede } = useSesion();
  const cliente = useQueryClient();
  const [ahora] = useState(() => Date.now());
  const [editando, setEditando] = useState<Pasarela | null>(null);

  const pasarelas = useQuery({
    queryKey: ['pagos-en-linea', 'pasarelas'],
    queryFn: () => api.get<Pasarela[]>('/pagos-en-linea/pasarelas'),
  });

  const cambiar = useMutation({
    mutationFn: (p: Pasarela) =>
      api.put<Pasarela>(`/pagos-en-linea/pasarelas/${p.codigo}`, {
        activa: !p.activa,
        orden: p.orden,
      }),
    onSuccess: (p) => {
      void cliente.invalidateQueries({ queryKey: ['pagos-en-linea'] });
      notificar.exito(`${p.nombre} ${p.activa ? 'activada' : 'apagada'}`);
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo cambiar'),
  });

  const olvidar = useMutation({
    mutationFn: (p: Pasarela) =>
      api.delete<Pasarela>(`/pagos-en-linea/pasarelas/${p.codigo}/credenciales`),
    onSuccess: (p) => {
      void cliente.invalidateQueries({ queryKey: ['pagos-en-linea'] });
      notificar.exito(`Credenciales de ${p.nombre} borradas`);
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudieron borrar'),
  });

  return (
    <>
      <EncabezadoDePagina
        icono={CreditCard}
        color="violeta"
        titulo="Pasarelas de pago"
        subtitulo="Con qué puede pagar el ciudadano en la ventanilla en línea"
      />

      <p className="mb-5 flex gap-2 rounded-xl border border-blue-200 bg-blue-50 p-4 text-xs uppercase leading-relaxed text-blue-900">
        <KeyRound className="mt-0.5 size-4 shrink-0" aria-hidden />
        La clave de cada pasarela se guarda cifrada y no se vuelve a mostrar: solo quedan sus
        últimos cuatro caracteres. Si una pasarela tiene sus credenciales en el servidor de la
        ventanilla, esas mandan y desde aquí no se cambian.
      </p>

      {pasarelas.isPending && (
        <p className="text-xs uppercase text-armazon-400">Cargando las pasarelas…</p>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {pasarelas.data?.map((p) => (
          <Tarjeta
            key={p.codigo}
            pasarela={p}
            ahora={ahora}
            puedeGestionar={puede('pagos_en_linea.gestionar')}
            cambiando={cambiar.isPending && cambiar.variables?.codigo === p.codigo}
            alCambiar={() => cambiar.mutate(p)}
            alEditar={() => setEditando(p)}
            alOlvidar={() => olvidar.mutate(p)}
          />
        ))}
      </div>

      {editando && (
        <DialogoDeCredenciales
          pasarela={editando}
          alCerrar={() => setEditando(null)}
          alGuardar={() => void cliente.invalidateQueries({ queryKey: ['pagos-en-linea'] })}
        />
      )}
    </>
  );
}

function Tarjeta({
  pasarela: p,
  ahora,
  puedeGestionar,
  cambiando,
  alCambiar,
  alEditar,
  alOlvidar,
}: {
  pasarela: Pasarela;
  ahora: number;
  puedeGestionar: boolean;
  cambiando: boolean;
  alCambiar: () => void;
  alEditar: () => void;
  alOlvidar: () => void;
}) {
  const proveedor = PROVEEDORES[p.codigo];
  const enElServidor = p.origen === 'servidor';
  const callada = !p.reportadaEn || ahora - new Date(p.reportadaEn).getTime() > 15 * 60_000;
  const entorno = p.entornoElegido ?? p.entorno;

  const credenciales =
    !p.configurada && !p.conCredencialesEnElPanel
      ? { texto: 'Sin credenciales', clase: 'text-armazon-500', Icono: KeyRound }
      : p.credencialesValidas === true
        ? { texto: 'Credenciales aceptadas', clase: 'text-marca-700', Icono: CheckCircle2 }
        : p.credencialesValidas === false
          ? { texto: 'La pasarela las rechaza', clase: 'text-alerta', Icono: CircleAlert }
          : { texto: 'Sin comprobar todavía', clase: 'text-amber-700', Icono: TriangleAlert };

  return (
    <article
      className={`flex flex-col rounded-2xl border bg-white p-5 shadow-sm ${p.activa ? 'border-marca-300' : 'border-armazon-200'}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-armazon-900">
            {p.codigo === 'SIMULADA' && (
              <FlaskConical className="size-4 text-amber-600" aria-hidden />
            )}
            {p.nombre}
          </h3>
          <p className="mt-0.5 flex items-center gap-1 text-[11px] uppercase text-armazon-400">
            {entorno === 'prod' ? (
              <span className="font-bold text-armazon-700">Producción</span>
            ) : entorno === 'simulada' ? (
              'No cobra nada'
            ) : entorno ? (
              'Pruebas'
            ) : (
              'Sin configurar'
            )}
            {enElServidor && (
              <>
                <span aria-hidden>·</span>
                <Server className="size-3" aria-hidden /> En el servidor
              </>
            )}
          </p>
        </div>
        <Interruptor
          encendido={p.activa}
          etiqueta={`${p.activa ? 'Apagar' : 'Activar'} ${p.nombre}`}
          deshabilitado={!puedeGestionar || cambiando}
          alPulsar={alCambiar}
        />
      </header>

      <p
        className={`mt-4 flex items-center gap-2 text-xs font-semibold uppercase ${credenciales.clase}`}
      >
        <credenciales.Icono className="size-4 shrink-0" aria-hidden />
        {credenciales.texto}
      </p>
      {p.detalle && (
        <p className="mt-1 line-clamp-3 text-[11px] uppercase leading-relaxed text-armazon-500">
          {p.detalle}
        </p>
      )}

      {proveedor && p.conCredencialesEnElPanel && (
        <dl className="mt-4 space-y-1 rounded-lg bg-armazon-50 p-3 text-[11px] uppercase text-armazon-600">
          <div className="flex justify-between gap-2">
            <dt className="shrink-0">{proveedor.usuario}</dt>
            <dd className="importe truncate font-semibold text-armazon-800" title={p.usuario ?? ''}>
              {p.usuario}
            </dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt>Clave</dt>
            <dd className="importe font-semibold text-armazon-800">•••• {p.secretoPista}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt>Dirección</dt>
            <dd className="truncate text-armazon-700" title={p.url ?? ''}>
              {(p.url ?? '').replace('https://', '')}
            </dd>
          </div>
          {p.credencialesEn && (
            <div className="flex justify-between gap-2">
              <dt>Cargadas</dt>
              <dd className="truncate">
                {fechaYHora(p.credencialesEn)} · {p.credencialesPor}
              </dd>
            </div>
          )}
        </dl>
      )}

      {proveedor && puedeGestionar && !enElServidor && (
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={alEditar}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-armazon-200 bg-white px-3 py-2 text-xs font-bold uppercase tracking-wide text-armazon-700 transition-colors hover:border-marca-300 hover:text-marca-700"
          >
            <KeyRound className="size-4" aria-hidden />
            {p.conCredencialesEnElPanel ? 'Cambiar credenciales' : 'Cargar credenciales'}
          </button>
          {p.conCredencialesEnElPanel && (
            <button
              type="button"
              onClick={alOlvidar}
              title={`Borrar las credenciales de ${p.nombre}`}
              aria-label={`Borrar las credenciales de ${p.nombre}`}
              className="rounded-lg border border-armazon-200 px-3 py-2 text-armazon-400 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-alerta"
            >
              <Trash2 className="size-4" aria-hidden />
            </button>
          )}
        </div>
      )}

      {proveedor && enElServidor && (
        <p className="mt-4 rounded-lg bg-armazon-50 px-3 py-2 text-[11px] uppercase leading-relaxed text-armazon-600">
          Sus credenciales están en el servidor de la ventanilla. Para cambiarlas hay que editarlas
          allí; desde el panel solo se enciende o se apaga.
        </p>
      )}

      {!proveedor && (
        <p className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-[11px] font-semibold uppercase leading-relaxed text-amber-800">
          Solo para desarrollo: no cobra nada y no lleva credenciales.
        </p>
      )}

      <p className="mt-auto pt-3 text-[10px] uppercase text-armazon-400">
        {p.reportadaEn ? (
          <span className={callada ? 'font-semibold text-amber-700' : undefined}>
            La ventanilla informó {fechaYHora(p.reportadaEn)}
          </span>
        ) : (
          'La ventanilla no ha informado todavía'
        )}
      </p>
    </article>
  );
}

function Interruptor({
  encendido,
  etiqueta,
  deshabilitado,
  alPulsar,
}: {
  encendido: boolean;
  etiqueta: string;
  deshabilitado: boolean;
  alPulsar: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={encendido}
      aria-label={etiqueta}
      title={etiqueta}
      disabled={deshabilitado}
      onClick={alPulsar}
      className={`inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        encendido ? 'border-marca-600 bg-marca-500' : 'border-armazon-300 bg-armazon-200'
      }`}
    >
      <span
        className={`inline-block size-4 rounded-full bg-white shadow transition-transform ${
          encendido ? 'translate-x-[22px]' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function DialogoDeCredenciales({
  pasarela: p,
  alCerrar,
  alGuardar,
}: {
  pasarela: Pasarela;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const proveedor = PROVEEDORES[p.codigo]!;
  const [entorno, setEntorno] = useState<'stg' | 'prod'>(
    p.entornoElegido === 'prod' ? 'prod' : 'stg',
  );
  const [usuario, setUsuario] = useState(p.usuario ?? '');
  const [secreto, setSecreto] = useState('');
  const [url, setUrl] = useState(p.url ?? proveedor.pruebas);

  function cambiarEntorno(nuevo: 'stg' | 'prod') {
    setEntorno(nuevo);

    if (url === proveedor.pruebas || url === proveedor.produccion || !url) {
      setUrl(nuevo === 'prod' ? proveedor.produccion : proveedor.pruebas);
    }
  }

  const guardar = useMutation({
    mutationFn: () =>
      api.put<Pasarela>(`/pagos-en-linea/pasarelas/${p.codigo}/credenciales`, {
        url: url.trim(),
        entorno,
        usuario: usuario.trim(),
        secreto: secreto.trim(),
      }),
    onSuccess: () => {
      alGuardar();
      alCerrar();
      notificar.exito(`Credenciales de ${p.nombre} guardadas`);
    },
  });
  const error = guardar.error instanceof ErrorDeApi ? guardar.error.message : null;

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo={`Credenciales de ${p.nombre}`}
      subtitulo={proveedor.ayuda}
      icono={KeyRound}
      ancho="estrecho"
    >
      <div className="space-y-3">
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm uppercase text-red-900"
          >
            {error}
          </p>
        )}

        <Campo etiqueta="Entorno" requerido>
          <div className="grid grid-cols-2 gap-2">
            {(['stg', 'prod'] as const).map((valor) => (
              <button
                key={valor}
                type="button"
                onClick={() => cambiarEntorno(valor)}
                aria-pressed={entorno === valor}
                className={`rounded-lg border px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors ${
                  entorno === valor
                    ? 'border-marca-500 bg-marca-50 text-marca-700'
                    : 'border-armazon-200 bg-white text-armazon-600 hover:bg-armazon-50'
                }`}
              >
                {valor === 'stg' ? 'Pruebas' : 'Producción'}
              </button>
            ))}
          </div>
        </Campo>

        <Campo etiqueta={proveedor.usuario} requerido>
          <input
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            maxLength={120}
            autoComplete="off"
            className={claseEntrada}
          />
        </Campo>

        <Campo
          etiqueta={proveedor.secreto}
          requerido
          ayuda={
            p.conCredencialesEnElPanel
              ? `SE REEMPLAZA LA ACTUAL (•••• ${p.secretoPista}). NO SE VUELVE A MOSTRAR.`
              : 'SE GUARDA CIFRADA Y NO SE VUELVE A MOSTRAR.'
          }
        >
          <input
            type="password"
            value={secreto}
            onChange={(e) => setSecreto(e.target.value)}
            maxLength={200}
            autoComplete="new-password"
            className={`${claseEntrada} importe`}
          />
        </Campo>

        <Campo etiqueta="Dirección de la pasarela" requerido ayuda="TIENE QUE EMPEZAR POR https://">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            maxLength={200}
            autoComplete="off"
            className={`${claseEntrada} importe`}
          />
        </Campo>

        {entorno === 'prod' && (
          <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs uppercase text-amber-900">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
            En producción se cobra de verdad a la tarjeta del ciudadano.
          </p>
        )}
      </div>

      <div className="mt-6 flex justify-end gap-2 border-t border-armazon-100 pt-5">
        <button
          type="button"
          onClick={alCerrar}
          className="rounded-lg px-4 py-2 text-sm font-semibold uppercase text-armazon-600 hover:bg-armazon-100"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => guardar.mutate()}
          disabled={
            guardar.isPending || !usuario.trim() || secreto.trim().length < 8 || !url.trim()
          }
          className="flex items-center gap-2 rounded-lg bg-marca-600 px-4 py-2 text-sm font-bold uppercase text-white hover:bg-marca-700 disabled:opacity-50"
        >
          <KeyRound className="size-4" aria-hidden />
          {guardar.isPending ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </Modal>
  );
}
